import React, { useEffect, useState } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { Loader2, TrendingUp, Users, Mail, DollarSign, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api, formatErrorDetail } from "@/lib/api";

const COLORS = ["#F26722", "#8B0000", "#1F6F8B", "#FFC857", "#E63946", "#6B9080"];

function StatCard({ icon: Icon, label, value, sub, color = "saffron" }) {
  const colorMap = {
    saffron: "text-saffron",
    deepred: "text-deepred",
    peacock: "text-peacock",
    gold: "text-marigold",
    emerald: "text-emerald-600",
  };
  return (
    <div className="bg-white border border-cream-200 p-6" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs uppercase tracking-[0.25em] text-foreground/60">{label}</div>
        <Icon className={`w-4 h-4 ${colorMap[color]}`} />
      </div>
      <div className="font-serif text-3xl">{value}</div>
      {sub && <div className="text-xs text-foreground/55 mt-1">{sub}</div>}
    </div>
  );
}

export default function AdminAnalytics() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/admin/stats")
      .then((r) => setStats(r.data))
      .catch((e) =>
        toast.error(formatErrorDetail(e.response?.data?.detail) || "Failed to load analytics")
      )
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="bg-white border border-cream-200 p-16 text-center" data-testid="analytics-loading">
        <Loader2 className="w-6 h-6 animate-spin inline text-saffron" />
      </div>
    );
  }
  if (!stats) return null;

  return (
    <div className="space-y-8" data-testid="analytics-panel">
      {/* TOP STATS */}
      <div>
        <h3 className="font-serif text-xl mb-4 text-foreground">Bookings & revenue</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            icon={DollarSign}
            label="Total revenue"
            value={`$${stats.revenue.total_usd.toLocaleString("en-US")}`}
            sub={`$${stats.revenue.last_30d_usd.toLocaleString("en-US")} in last 30 days`}
            color="saffron"
          />
          <StatCard
            icon={TrendingUp}
            label="Total bookings"
            value={stats.bookings.total}
            sub={`${stats.bookings.paid} paid · ${stats.bookings.pending} pending`}
            color="deepred"
          />
          <StatCard
            icon={Users}
            label="Last 7 days"
            value={stats.bookings.last_7d}
            sub={`${stats.bookings.last_30d} in last 30 days`}
            color="peacock"
          />
          <StatCard
            icon={DollarSign}
            label="Avg booking"
            value={`$${stats.revenue.avg_booking_usd.toLocaleString("en-US")}`}
            sub="per confirmed booking"
            color="gold"
          />
        </div>
      </div>

      {/* DAILY BOOKINGS CHART */}
      <div className="bg-white border border-cream-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-serif text-lg">Bookings — last 30 days</h3>
            <p className="text-xs text-foreground/60">Daily booking volume & revenue trend</p>
          </div>
        </div>
        <div className="h-72" data-testid="daily-chart">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={stats.charts.daily} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F5EFE0" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 10, fill: "#6B5B3F" }}
                tickFormatter={(d) => {
                  const dt = new Date(d);
                  return `${dt.getMonth() + 1}/${dt.getDate()}`;
                }}
              />
              <YAxis yAxisId="left" tick={{ fontSize: 10, fill: "#6B5B3F" }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fill: "#6B5B3F" }} />
              <Tooltip
                contentStyle={{ fontSize: "12px", border: "1px solid #F5EFE0", borderRadius: "2px" }}
                labelFormatter={(d) => new Date(d).toLocaleDateString()}
                formatter={(val, name) => (name === "revenue" ? [`$${val}`, "Revenue"] : [val, "Bookings"])}
              />
              <Line yAxisId="left" type="monotone" dataKey="bookings" stroke="#F26722" strokeWidth={2} dot={{ r: 3 }} />
              <Line yAxisId="right" type="monotone" dataKey="revenue" stroke="#8B0000" strokeWidth={2} dot={{ r: 3 }} strokeDasharray="4 4" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* TOP TOURS */}
        <div className="bg-white border border-cream-200 p-6">
          <h3 className="font-serif text-lg mb-4">Top-booked tours</h3>
          {stats.charts.top_tours.length === 0 ? (
            <p className="text-sm text-foreground/55 italic py-8 text-center">
              No tour bookings yet.
            </p>
          ) : (
            <div className="h-72" data-testid="top-tours-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.charts.top_tours} layout="vertical" margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F5EFE0" />
                  <XAxis type="number" tick={{ fontSize: 10, fill: "#6B5B3F" }} />
                  <YAxis
                    type="category"
                    dataKey="tour"
                    width={140}
                    tick={{ fontSize: 10, fill: "#1A1A1A" }}
                    tickFormatter={(t) => (t.length > 22 ? t.slice(0, 22) + "…" : t)}
                  />
                  <Tooltip contentStyle={{ fontSize: "12px" }} />
                  <Bar dataKey="bookings" fill="#F26722" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* INQUIRY STATUS BREAKDOWN */}
        <div className="bg-white border border-cream-200 p-6">
          <h3 className="font-serif text-lg mb-4">Inquiries by status</h3>
          {stats.charts.inquiries_by_status.length === 0 ? (
            <p className="text-sm text-foreground/55 italic py-8 text-center">
              No inquiries yet.
            </p>
          ) : (
            <div className="h-72" data-testid="inquiry-status-chart">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={stats.charts.inquiries_by_status}
                    dataKey="count"
                    nameKey="status"
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    label={(entry) => `${entry.status} (${entry.count})`}
                    labelLine={false}
                  >
                    {stats.charts.inquiries_by_status.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Legend wrapperStyle={{ fontSize: "11px" }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* LEAD FUNNEL */}
      <div>
        <h3 className="font-serif text-xl mb-4 text-foreground">Lead funnel</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            icon={Mail}
            label="Total inquiries"
            value={stats.leads.total_inquiries}
            sub={`${stats.leads.last_30d} in last 30 days`}
            color="saffron"
          />
          <StatCard
            icon={TrendingUp}
            label="Converted"
            value={stats.leads.converted}
            sub="Inquiries turned into bookings"
            color="emerald"
          />
          <StatCard
            icon={TrendingUp}
            label="Conversion rate"
            value={`${stats.leads.conversion_rate_pct}%`}
            sub="Inquiries → bookings"
            color="deepred"
          />
          <StatCard
            icon={Mail}
            label="Newsletter subs"
            value={stats.leads.newsletter_subscribers}
            sub="Active email subscribers"
            color="peacock"
          />
        </div>
      </div>

      {/* TOP SOURCE COUNTRIES */}
      <div className="bg-white border border-cream-200 p-6">
        <h3 className="font-serif text-lg mb-4 flex items-center gap-2">
          <MapPin className="w-4 h-4 text-saffron" /> Top inquiry source countries
        </h3>
        {stats.charts.top_countries.length === 0 ? (
          <p className="text-sm text-foreground/55 italic py-6">No country data yet.</p>
        ) : (
          <div className="space-y-3" data-testid="top-countries">
            {stats.charts.top_countries.map((c, i) => {
              const maxCount = Math.max(...stats.charts.top_countries.map((x) => x.count));
              const pct = Math.round((c.count / maxCount) * 100);
              return (
                <div key={c.country} data-testid={`country-row-${i}`}>
                  <div className="flex justify-between text-sm mb-1">
                    <span>{c.country}</span>
                    <span className="text-foreground/55">{c.count} inquiries</span>
                  </div>
                  <div className="h-2 bg-cream-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-saffron to-deepred"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
